/**
 * Compact-layout route strip (issue #111): while BOTH the compact layout
 * signal (issue #109's `ui/layoutMode.ts` `createCompactSignal`) and Route
 * nav mode are active at once, collapses the desktop `#panel-route` card
 * (six buttons + scrubber + readout + hint, ~420px) into a single-line
 * bottom strip — `[☰ More][Play/Pause][scrubber][Aim]` — that replaces the
 * caveat/☰-More row for the duration (issue #132 relocated the caveat itself
 * permanently into the bottom sheet, so this module no longer tracks it at
 * all), and moves the remaining route controls (−/+/Reset/Loop/?/hint/
 * readout) into a route section of the compact bottom sheet
 * (`ui/mobileSheet.ts`). Any other combination — desktop (in or out of
 * Route), or compact in a non-Route nav mode — is left exactly as
 * `main.ts`'s ordinary Route-panel visibility already decides it; this
 * module only ever acts on the compact+Route corner, on either axis flipping
 * independently (a resize across the breakpoint mid-playback, or a nav-mode
 * switch while already compact).
 *
 * Same re-parent/restore mechanism as `ui/mobileSheet.ts`: moves the real,
 * live nodes — every listener and all persisted playback state
 * (`routePlayer`'s speed/loop/aim, the scrubber's current value) keeps
 * working untouched, and no control ever exists twice — and pins each
 * element's original DOM position with an adjacent marker comment so
 * leaving the compact+Route corner from EITHER axis restores every control
 * to its exact original slot, idempotently and repeatably (mirrors #109's
 * round-trip guarantee).
 *
 * Width fallback (issue #132): the strip's four controls are fixed-size
 * (each has an explicit `min-width: 44px` touch-target floor — see
 * style.css), so on a narrow viewport they can outgrow the column
 * `--minimap-compact-width` leaves them once the minimap grew 25% larger,
 * visually colliding with it. `refreshFit()` measures the real gap between
 * the strip's last control and the minimap's own left edge and, if it would
 * collide, flips a CSS override (via `onNarrowMinimapChange`) that reverts
 * the minimap to its pre-#132 smaller width for as long as the strip stays
 * active — a real bounding-box comparison rather than a hardcoded viewport
 * width, so it stays correct however the strip's own content changes later.
 * Called at layout time only (entering the compact+Route corner, and from
 * main.ts's window resize handler) — never from the render loop.
 */

export interface RouteStripConfig {
  /** The single-line strip container (`#route-strip`), hidden outside the
   * compact+Route corner. */
  stripEl: HTMLElement;
  /** Desktop's route panel (`#panel-route`). The caller decides whether
   * Route nav mode is active via `setRouteActive`; this module derives
   * `#panel-route`'s visibility from that plus its own compact state — the
   * caller never toggles `#panel-route`'s own hidden class directly. */
  panelEl: HTMLElement;
  /** The "☰ More" button (`#mobile-sheet-toggle`) — moved into the strip as
   * its first element while active. Same live node, so
   * `ui/mobileSheet.ts`'s click listener on it keeps working unmodified. */
  toggleEl: HTMLElement;
  /** Elements moved into the strip, in display order, directly after
   * `toggleEl`: Play/Pause, the scrubber, Aim. The LAST element is the one
   * `refreshFit()` measures against the minimap's left edge. */
  stripItems: readonly HTMLElement[];
  /** The route section inside the compact bottom sheet's content
   * (`#route-sheet-section`) that `sheetItems` move into. */
  sheetSectionEl: HTMLElement;
  /** Elements moved into `sheetSectionEl`, in display order: −, +, Reset,
   * Loop, the `?` hint toggle, the hint text, and the readout. */
  sheetItems: readonly HTMLElement[];
  /** The minimap's container (`#osm-minimap`) — read-only, only ever used to
   * measure its current left edge for the width fallback above. */
  minimapEl: HTMLElement;
  /** Fires whenever the width-fallback decision changes (see `refreshFit`).
   * `main.ts` uses this to toggle the CSS override that reverts
   * `--minimap-compact-width` to its pre-#132 smaller value. Never fires
   * while inactive. */
  onNarrowMinimapChange?: (narrow: boolean) => void;
}

export class RouteStrip {
  private readonly stripEl: HTMLElement;
  private readonly panelEl: HTMLElement;
  private readonly toggleEl: HTMLElement;
  private readonly stripItems: readonly HTMLElement[];
  private readonly sheetSectionEl: HTMLElement;
  private readonly sheetItems: readonly HTMLElement[];
  private readonly minimapEl: HTMLElement;
  private readonly onNarrowMinimapChange?: (narrow: boolean) => void;

  /** Restore callbacks keyed by the element they restore, one per currently
   * pinned element. Empty whenever not active. */
  private readonly anchors = new Map<HTMLElement, () => void>();

  private compact = false;
  private routeActive = false;
  private narrowMinimap = false;

  constructor(config: RouteStripConfig) {
    this.stripEl = config.stripEl;
    this.panelEl = config.panelEl;
    this.toggleEl = config.toggleEl;
    this.stripItems = config.stripItems;
    this.sheetSectionEl = config.sheetSectionEl;
    this.sheetItems = config.sheetItems;
    this.minimapEl = config.minimapEl;
    this.onNarrowMinimapChange = config.onNarrowMinimapChange;
    this.render();
  }

  /** Whether the strip (and sheet route section) currently replaces the
   * desktop route panel — true only while both compact and Route nav mode
   * are active at once. */
  get isActive(): boolean {
    return this.compact && this.routeActive;
  }

  /**
   * Applies the compact/desktop layout signal (issue #109's
   * `createCompactSignal`). Idempotent: a repeated call with the same value
   * never re-parents a second time. A resize back to desktop mid-playback
   * restores every control to `#panel-route`; a resize into compact
   * mid-playback (Route already active) moves them into the strip/sheet —
   * either way playback itself (`routePlayer`) is untouched, since the
   * nodes move rather than being recreated.
   */
  setCompact(compact: boolean): void {
    if (compact === this.compact) return;
    this.transition(() => {
      this.compact = compact;
    });
  }

  /**
   * Applies whether Route is the active nav mode. Idempotent: a repeated
   * call with the same value never re-parents a second time. Leaving Route
   * mode while compact empties the sheet's route section, exactly as leaving
   * Route on desktop just hides `#panel-route`.
   */
  setRouteActive(routeActive: boolean): void {
    if (routeActive === this.routeActive) return;
    this.transition(() => {
      this.routeActive = routeActive;
    });
  }

  private transition(applyChange: () => void): void {
    const wasActive = this.isActive;
    applyChange();
    const nowActive = this.isActive;
    if (nowActive && !wasActive) this.enter();
    else if (wasActive && !nowActive) this.leave();
    this.render();
    if (nowActive) this.refreshFit();
  }

  private enter(): void {
    this.pin(this.toggleEl);
    this.stripEl.appendChild(this.toggleEl);
    for (const el of this.stripItems) {
      this.pin(el);
      this.stripEl.appendChild(el);
    }
    for (const el of this.sheetItems) {
      this.pin(el);
      this.sheetSectionEl.appendChild(el);
    }
  }

  private leave(): void {
    this.restore(this.toggleEl);
    for (const el of this.stripItems) this.restore(el);
    for (const el of this.sheetItems) this.restore(el);
    this.setNarrowMinimap(false);
  }

  /**
   * Re-decides the width fallback (see the class doc) at the current
   * viewport size. Always re-probes against the full-size (#132) minimap
   * first, even if currently narrowed, so a widened viewport can revert back
   * to it — a live bounding-box comparison rather than a remembered
   * viewport-width threshold. A no-op while inactive (desktop, or compact
   * without Route); safe to call from a window resize handler, since it only
   * ever runs at layout time, never per frame.
   */
  refreshFit(): void {
    if (!this.isActive) return;
    if (this.narrowMinimap) this.setNarrowMinimap(false);
    const lastItem = this.stripItems[this.stripItems.length - 1];
    const fits = lastItem.getBoundingClientRect().right <= this.minimapEl.getBoundingClientRect().left;
    this.setNarrowMinimap(!fits);
  }

  private setNarrowMinimap(narrow: boolean): void {
    if (narrow === this.narrowMinimap) return;
    this.narrowMinimap = narrow;
    this.onNarrowMinimapChange?.(narrow);
  }

  private pin(el: HTMLElement): void {
    const marker = document.createComment('route-strip-anchor');
    el.parentNode?.insertBefore(marker, el);
    this.anchors.set(el, () => {
      marker.parentNode?.insertBefore(el, marker);
      marker.remove();
    });
  }

  private restore(el: HTMLElement): void {
    const detach = this.anchors.get(el);
    if (!detach) return;
    this.anchors.delete(el);
    detach();
  }

  /**
   * `#panel-route` is visible only on desktop while Route is active
   * (`routeActive && !compact`) — the one formula covers all four
   * (compact, routeActive) combinations without extra state: never active
   * means never shown; compact+active means the strip takes its place.
   */
  private render(): void {
    const active = this.isActive;
    this.stripEl.classList.toggle('hidden', !active);
    this.panelEl.classList.toggle('hidden', !(this.routeActive && !this.compact));
  }
}
